import {useContext, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Card, CardContent, Typography, Button, Chip, CircularProgress, Stack} from "@mui/material";
import {STATUS_COLORS} from "../../../config/statusColors";
import {HugeiconsIcon} from "@hugeicons/react";
import {Add01Icon, Clock01Icon, DatabaseIcon, File02Icon, Message01Icon, ServerIcon} from "@hugeicons/core-free-icons";
import {Context} from "../../../context/AppContext";
import {KnowledgeManagerLayout} from "./components/KnowledgeManagerLayout";
import {CreateTopicModal} from "./components/CreateTopicModal";

const formatShortDate = (isoString) => {
    if (!isoString) return "";
    const d = new Date(isoString);
    return d.toLocaleDateString("en-US", {month: "short", day: "numeric"});
};

const serverStatusColor = (status) => {
    if (status === "online") return {bg: STATUS_COLORS.success.bg, color: STATUS_COLORS.success.text};
    if (status === "connecting") return {bg: STATUS_COLORS.warning.bg, color: STATUS_COLORS.warning.text};
    return {bg: STATUS_COLORS.error.bg, color: STATUS_COLORS.error.text};
};

export const KnowledgeManagerPage = ({project}) => {
    const {projectId} = useParams();
    const navigate = useNavigate();
    const {state, getAllKbTopics} = useContext(Context);
    const {kbTopics, kbTopicsLoading} = state;
    const [createOpen, setCreateOpen] = useState(false);

    const totalDocs = kbTopics.reduce((acc, t) => acc + (t.documentCount || 0), 0);
    const totalQueries = kbTopics.reduce((acc, t) => acc + (t.queryCount || 0), 0);
    const onlineServers = kbTopics.filter(t => t.server?.status === "online").length;

    if (kbTopicsLoading && kbTopics.length === 0) {
        return (
            <Box sx={{flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300}}>
                <CircularProgress size={28}/>
            </Box>
        );
    }

    return (
        <KnowledgeManagerLayout project={project}>
            <Box sx={{flex: 1, overflow: "auto"}}>
                <Box sx={{p: 3, maxWidth: 1200, mx: "auto"}}>


                    <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{mb: 3}}>
                        <Box>
                            <Typography variant={"h5"} fontWeight={700}>Knowledge Base Dashboard</Typography>
                            <Typography variant={"body2"} color={"text.secondary"} sx={{mt: 0.5}}>
                                Manage your VoiceBot & ChatBot knowledge bases powered by LightRAG
                            </Typography>
                        </Box>
                        <Button
                            variant={"modal"}
                            startIcon={<HugeiconsIcon icon={Add01Icon} size={16}/>}
                            onClick={() => setCreateOpen(true)}

                        >
                            New Topic
                        </Button>
                    </Stack>


                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr"},
                        gap: 2,
                        mb: 4,
                    }}>
                        <StatCard label={"Topics"} value={kbTopics.length}
                                  icon={<HugeiconsIcon icon={DatabaseIcon} size={20} color={"#4f46e5"}/>} bg={"#eef2ff"}/>
                        <StatCard label={"Documents"} value={totalDocs}
                                  icon={<HugeiconsIcon icon={File02Icon} size={20} color={"#059669"}/>} bg={"#ecfdf5"}/>
                        <StatCard label={"Total Queries"} value={totalQueries}
                                  icon={<HugeiconsIcon icon={Message01Icon} size={20} color={"#d97706"}/>} bg={"#fffbeb"}/>
                        <StatCard
                            label={"Servers Online"}
                            value={<>{onlineServers}<Typography component={"span"} fontSize={14}
                                                                color={"text.secondary"}>/{kbTopics.length}</Typography></>}
                            icon={<HugeiconsIcon icon={ServerIcon} size={20} color={"#0284c7"}/>}
                            bg={"#f0f9ff"}
                        />
                    </Box>


                    <Typography variant={"h6"} fontWeight={700} sx={{mb: 2}}>Topics</Typography>
                    <Box sx={{
                        display: "grid",
                        gridTemplateColumns: {xs: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr"},
                        gap: 2,
                    }}>
                        {kbTopics.map(topic => {
                            const statusStyle = topic.server ? serverStatusColor(topic.server.status) : null;
                            return (
                                <Card
                                    key={topic.id}
                                    sx={{
                                        cursor: "pointer",
                                        borderRadius: 2,
                                        border: "1px solid",
                                        borderColor: "divider",
                                        boxShadow: "none",
                                        "&:hover": {
                                            borderColor: "#fff",
                                            boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                        },
                                    }}
                                    onClick={() => navigate(`/project/${projectId}/knowledge-manager/${topic.id}`)}
                                >
                                    <CardContent>
                                        <Stack direction={"row"} alignItems={"flex-start"}
                                               justifyContent={"space-between"} sx={{mb: 1.5}}>
                                            <Stack direction={"row"} alignItems={"center"} gap={1}>
                                                <Box sx={{width: 12, height: 12, borderRadius: "50%", bgcolor: topic.color, flexShrink: 0}} />
                                                <Typography fontWeight={600} fontSize={15}>{topic.name}</Typography>
                                            </Stack>
                                            {topic.server && (
                                                <Chip
                                                    label={topic.server.status}
                                                    size={"small"}
                                                    sx={{
                                                        fontSize: 10, height: 20,
                                                        bgcolor: statusStyle.bg, color: statusStyle.color,
                                                    }}
                                                />
                                            )}
                                        </Stack>

                                        <Typography
                                            fontSize={12} color={"text.secondary"} sx={{
                                            mb: 1.5,
                                            display: "-webkit-box",
                                            WebkitLineClamp: 2,
                                            WebkitBoxOrient: "vertical",
                                            overflow: "hidden",
                                        }}>
                                            {topic.description}
                                        </Typography>

                                        <Stack direction={"row"} gap={2} sx={{fontSize: 11, color: "text.secondary"}}>
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                <HugeiconsIcon icon={File02Icon} size={12}/> {topic.documentCount} docs
                                            </Stack>
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                <HugeiconsIcon icon={Message01Icon} size={12}/> {topic.queryCount} queries
                                            </Stack>
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}>
                                                <HugeiconsIcon icon={Clock01Icon} size={12}/> {formatShortDate(topic.updatedAt)}
                                            </Stack>
                                        </Stack>

                                        <Stack direction={"row"} flexWrap={"wrap"} gap={0.5} sx={{mt: 1.5}}>
                                            {topic.tags?.map(tag => (
                                                <Chip key={tag} label={tag} variant={"outlined"} size={"small"}
                                                      sx={{fontSize: 10, height: 20}}/>
                                            ))}
                                        </Stack>

                                        {topic.server && (
                                            <Stack direction={"row"} alignItems={"center"} gap={0.5}
                                                   sx={{mt: 1.5, fontSize: 11, color: "text.secondary"}}>
                                                <HugeiconsIcon icon={ServerIcon} size={12}/>
                                                <Typography noWrap fontSize={11} color={"text.secondary"}>
                                                    {topic.server.host}:{topic.server.port}
                                                </Typography>
                                            </Stack>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}


                        <Card
                            variant={"outlined"}
                            sx={{
                                cursor: "pointer",
                                borderStyle: "dashed",
                                borderColor: "divider",
                                borderRadius: 3,
                                boxShadow: "none",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: 200,
                                "&:hover": {
                                    boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                                },
                            }}
                            onClick={() => setCreateOpen(true)}
                        >
                            <Box sx={{textAlign: "center", color: "text.secondary"}}>
                                <HugeiconsIcon icon={Add01Icon} size={32} style={{opacity: 0.4, marginBottom: 8}}/>
                                <Typography fontSize={13}>Create New Topic</Typography>
                                <Typography fontSize={11} sx={{opacity: 0.6, mt: 0.5}}>Add a new LightRAG
                                    workspace</Typography>
                            </Box>
                        </Card>
                    </Box>

                    {kbTopics.length === 0 && (
                        <Typography color={"text.secondary"} sx={{mt: 2}}>No topics created yet.</Typography>
                    )}
                </Box>
            </Box>
            <CreateTopicModal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                onCreated={() => getAllKbTopics(project?.settings?.organization)}
                project={project}
            />
        </KnowledgeManagerLayout>
    );

};

const StatCard = ({label, value, icon, bg}) => (
    <Card sx={{borderRadius: 2, border: "1px solid", borderColor: "divider", boxShadow: "none"}}>
        <CardContent sx={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>
            <Box>
                <Typography fontSize={12} color={"text.secondary"}>{label}</Typography>
                <Typography fontSize={28} sx={{mt: 0.5}}>{value}</Typography>
            </Box>
            <Box sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                bgcolor: bg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
            }}>
                {icon}
            </Box>
        </CardContent>
    </Card>
);
